import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { doc, setDoc } from 'firebase/firestore';
import { db, storage, APP_LOGO_STORAGE_PATH } from './firebase';
import { resizeImage, blobToFile } from '../utils/imageResize';

// Collection for app settings
const settingsRef = doc(db, 'app_settings', 'general');

/**
 * Uploads a logo image to Firebase Storage and saves its URL in Firestore
 * @param {File} logoFile - The logo image file to upload
 * @param {number} maxSize - Maximum width/height in pixels (default 150)
 * @returns {Promise<string>} A promise that resolves to the logo URL
 */
export const uploadLogo = async (logoFile, maxSize = 150) => {
  try {
    // Resize the image before upload
    console.log('Resizing logo image...');
    const resizedBlob = await resizeImage(logoFile, maxSize, maxSize, 0.9);
    const resizedFile = blobToFile(resizedBlob, logoFile.name);
    
    console.log(`Original size: ${(logoFile.size / 1024).toFixed(2)}KB`);
    console.log(`Resized size: ${(resizedFile.size / 1024).toFixed(2)}KB`);
    
    // Create a reference to the logo storage location
    const storageRef = ref(storage, APP_LOGO_STORAGE_PATH);
    
    // Upload the resized file
    console.log('Uploading resized logo to Firebase Storage...');
    const uploadResult = await uploadBytes(storageRef, resizedFile);
    console.log('Logo uploaded successfully:', uploadResult);
    
    // Get the download URL
    const logoUrl = await getDownloadURL(uploadResult.ref);
    console.log('Logo URL obtained:', logoUrl);
    
    // Save the logo URL and metadata to Firestore for easy retrieval
    await setDoc(settingsRef, {
      logoUrl,
      lastUpdated: new Date().toISOString(),
      filename: logoFile.name,
      maxSize: maxSize
    }, { merge: true });
    
    // Also save to localStorage for immediate use and offline access
    try {
      localStorage.setItem('appLogoUrl', logoUrl);
      localStorage.setItem('appLogoUpdated', new Date().toISOString());
    } catch (err) {
      console.warn('Failed to save logo URL to localStorage:', err);
    }
    
    return logoUrl;
  } catch (error) {
    console.error('Error uploading logo:', error);
    throw error;
  }
};

/**
 * Gets the current logo URL from Firestore or localStorage
 * @returns {Promise<string>} A promise that resolves to the logo URL
 */
export const getLogoUrl = async () => {
  try {
    // First try to get from localStorage for fastest response
    const cachedLogoUrl = localStorage.getItem('appLogoUrl');
    
    if (cachedLogoUrl) {
      console.log('Using cached logo URL from localStorage');
      return cachedLogoUrl;
    }
    
    // Fall back to directly getting the URL from Storage
    console.log('Getting logo URL from Firebase Storage...');
    const storageRef = ref(storage, APP_LOGO_STORAGE_PATH);
    const logoUrl = await getDownloadURL(storageRef);
    
    // Cache the URL in localStorage
    try {
      localStorage.setItem('appLogoUrl', logoUrl);
      localStorage.setItem('appLogoUpdated', new Date().toISOString());
    } catch (err) {
      console.warn('Failed to save logo URL to localStorage:', err);
    }
    
    return logoUrl;
  } catch (error) {
    console.error('Error getting logo URL:', error);
    
    // Return null if no logo has been uploaded yet
    return null;
  }
};